/** @jsxImportSource ../i18n */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useReducer, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowRight, Check, Radio, Settings2 } from 'lucide-react';
import api from '../services/api';
import { applyLiveEvent, emptyLiveState, followExpertRun, visibleText } from '../services/expertLive';
import { reportDate } from '../components/reportShared';
import { MarkdownProse } from '../components/markdown/MarkdownProse';
import { MascotAvatar } from '../components/mascotCatalog';
import DebateTable from '../components/DebateTable';
import LiveTyping from '../components/LiveTyping';
import { computeMoods, moodSignature, panelMembers } from '../services/expertMoods';
import { cx } from '../theme/cx';
import { Badge, Banner, Button, Card, PageHeader, Progress, Spinner } from '../components/ui';

const RECONNECT_MS = 3000;
const IDLE_CHECK_MS = 20000;
const NEAR_BOTTOM_PX = 120;

const nearBottom = (el) => el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;

// Brings the end of the feed to the bottom of the window (the page scrolls
// the window, never <main>). Not scrollIntoView: Chrome moves the keyboard's
// starting point to the scrolled element, so Tab on a followed feed would
// jump past the whole page.
const revealFeedEnd = (el) => {
  if (!el) return;
  window.scrollTo({ top: window.scrollY + el.getBoundingClientRect().bottom - window.innerHeight });
};

// FollowBox keeps a scrollable box pinned to its latest text (pin() is called
// as the text grows), unless the reader scrolled up inside it.
const FollowBox = forwardRef(({ className, children }, handle) => {
  const ref = useRef(null);
  const pinned = useRef(true);
  useImperativeHandle(handle, () => ({
    pin: () => { if (pinned.current && ref.current) ref.current.scrollTop = ref.current.scrollHeight; },
  }), []);
  return (
    <div ref={ref} className={className} data-testid="follow-box"
      onScroll={(e) => { pinned.current = nearBottom(e.currentTarget); }}>
      {children}
    </div>
  );
});

const STEPS = [
  ['research', 'Recherche'],
  ['analysis', 'Analyses'],
  ['agenda', 'Ordre du jour'],
  ['roundtable', 'Table ronde'],
  ['draft', 'Projet commun'],
  ['review', 'Relectures'],
  ['done', 'Terminé'],
];
// An on-demand debate ends with the rapporteur's conclusion, without reviews.
const TOPIC_STEPS = [...STEPS.slice(0, 4), ['draft', 'Conclusion'], ['done', 'Terminé']];
// The pilot's own debate stops at the decisions and its plan.
const PILOT_STEPS = [...STEPS.slice(0, 4), ['done', 'Terminé']];
const stepsFor = (kind) => (kind === 'topic' ? TOPIC_STEPS : kind === 'pilot' ? PILOT_STEPS : STEPS);
const stepIndex = (phase, steps = STEPS) => (phase === 'final' ? steps.findIndex(([id]) => id === 'review') : Math.max(0, steps.findIndex(([id]) => id === phase)));

const PHASE_LABELS = {
  analysis: 'Analyse individuelle',
  agenda: 'Modération de l\'ordre du jour',
  roundtable: 'Table ronde',
  outcome: 'Issue des arbitrages',
  draft: 'Projet de rapport commun',
  review: 'Relecture',
  final: 'Version finale',
};
const phaseLabel = (item) => `${PHASE_LABELS[item.phase] || item.phase}${item.phase === 'roundtable' && item.round ? ` — tour ${item.round}` : ''}`;
// Long texts scroll inside their bubble so the discussion stays readable.
const LONG = new Set(['analysis', 'draft', 'final']);

const Markdown = ({ children }) => <MarkdownProse variant="report" skipHtml>{children}</MarkdownProse>;

// Progress rail: done steps get a gold check, the current one a solid gold
// dot. Each item's text stays exactly its label (plus the round number).
const Stepper = ({ run, kind }) => {
  const steps = stepsFor(kind);
  const current = stepIndex(run?.phase, steps);
  return (
    <ol className="flex flex-wrap items-center gap-x-2 gap-y-3" aria-label="Avancement du débat">
      {steps.map(([id, label], i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li key={id} aria-current={active ? 'step' : undefined}
            className={cx('flex items-center gap-2 text-xs font-medium', active ? 'text-fg' : done ? 'text-fg-muted' : 'text-fg-faint')}>
            {i > 0 && <span aria-hidden="true" className={cx('h-px w-4 sm:w-6', done || active ? 'bg-gold-600/70' : 'bg-line')} />}
            <span aria-hidden="true" className={cx(
              'inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border',
              active ? 'border-gold-500 bg-gold-500 shadow-[0_0_0_4px_rgb(var(--c-gold-500)/0.15)]' : done ? 'border-gold-600/60 text-gold-600' : 'border-line-strong',
            )}>
              {done && <Check size={11} strokeWidth={3} />}
            </span>
            {label}{id === 'roundtable' && active && run?.phase_round ? ` · tour ${run.phase_round}` : ''}
          </li>
        );
      })}
    </ol>
  );
};

const VERDICT_TONES = { approve: ['Approuvé', 'gain'], reservations: ['Réserves', 'warn'] };

const Bubble = ({ item, live, onGrow }) => {
  const text = live ? visibleText(item.text || '') : item.content;
  const box = useRef(null);
  const grow = useCallback(() => { box.current?.pin(); onGrow?.(); }, [onGrow]);
  const body = item.error
    ? <p className="break-words text-sm text-loss-300">Indisponible : {item.error}</p>
    : live && text ? <LiveTyping text={text} complete={Boolean(item.done)} Markdown={Markdown} onGrow={grow} caret={!item.done} />
      : text ? <Markdown>{text}</Markdown> : <p className="text-sm italic text-fg-subtle">{live ? 'réfléchit…' : 'Contenu vide.'}</p>;
  const collapsed = !live && LONG.has(item.phase);
  const [verdictLabel, verdictTone] = VERDICT_TONES[item.verdict] || [item.verdict, 'neutral'];
  return (
    <article className="flex gap-3" data-testid={live ? 'live-stream' : `live-entry-${item.phase}`}>
      <MascotAvatar variant={item.mascot_variant} name={item.expert_name} size="md" />
      <div className={cx('min-w-0 flex-1 rounded-card border px-4 py-3.5 sm:px-5', live ? 'border-gold-500/40 bg-gold-950/40' : 'border-line bg-surface-1')}>
        <header className="mb-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm">
          <span className="font-medium text-fg">{item.expert_name}</span>
          <span className="text-fg-subtle">{phaseLabel(item)}</span>
          {item.verdict && <Badge tone={verdictTone}>{verdictLabel}</Badge>}
          {live && !item.done && (
            <span className="flex items-center gap-1.5 text-xs font-medium text-gold-300">
              <span className="h-1.5 w-1.5 animate-pulse-soft rounded-full bg-gold-400" aria-hidden="true" />écrit en direct
            </span>
          )}
        </header>
        {collapsed ? (
          <details className="group">
            <summary className="cursor-pointer list-none text-sm font-medium text-gold-300 transition-colors hover:text-gold-200 [&::-webkit-details-marker]:hidden">Afficher le texte</summary>
            <div className="mt-3">{body}</div>
          </details>
        ) : (
          <FollowBox ref={box} className={live && LONG.has(item.phase) ? 'max-h-72 overflow-y-auto pr-1' : ''}>
            {body}
          </FollowBox>
        )}
      </div>
    </article>
  );
};

const parseDetail = (detail) => {
  try {
    const d = JSON.parse(detail || '');
    return Number.isFinite(d?.total) && d.total > 0 ? d : null;
  } catch {
    return null;
  }
};

// Phase 1: which research briefs are done and which are being prepared.
const ResearchProgress = ({ detail }) => {
  const d = parseDetail(detail);
  if (!d) {
    return <Spinner size={14} label="Recherche des données de marché et des actualités…" />;
  }
  return (
    <Card className="space-y-3" role="status" data-testid="research-progress">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="flex items-center gap-2 text-fg-body"><Spinner size={14} />Recherche et synthèse des sources</span>
        <span className="num text-fg-muted">{d.done}/{d.total}</span>
      </div>
      <Progress value={Math.min(d.done, d.total)} max={d.total} />
      {Array.isArray(d.active) && d.active.length > 0 && (
        <p className="text-xs text-fg-subtle">En cours : {d.active.join(' · ')}</p>
      )}
    </Card>
  );
};

const reducer = (state, action) => (action.type === 'reset' ? { ...emptyLiveState, ...action.state } : applyLiveEvent(state, action.name, action.payload));

// Real-time view of today's expert debate. Watching never starts a run.
const ExpertLive = () => {
  // ?run=<id> follows one run (an on-demand debate just launched); otherwise
  // the latest run.
  const [params] = useSearchParams();
  const pinnedRun = params.get('run');
  const [latest, setLatest] = useState(undefined); // undefined = loading, null = no run
  const [error, setError] = useState('');
  const [connection, setConnection] = useState('idle'); // idle | live | reconnecting
  const [state, dispatch] = useReducer(reducer, emptyLiveState);
  const [check, setCheck] = useState(0);
  const [following, setFollowing] = useState(true);
  const feedEnd = useRef(null);
  const stick = useRef(true);

  // Find the latest run; while nothing runs, look again periodically.
  useEffect(() => {
    let active = true;
    const request = pinnedRun
      ? api.get(`/analysis/expert-reports/${pinnedRun}`).then((res) => res.data || null)
      : api.get('/analysis/expert-reports', { params: { limit: 1 } }).then((res) => (Array.isArray(res.data) && res.data[0] ? res.data[0] : null));
    request
      .then((run) => { if (active) { setLatest(run); setError(''); } })
      .catch(() => { if (active) setError('Impossible de charger les débats.'); });
    return () => { active = false; };
  }, [check, pinnedRun]);

  const running = latest?.status === 'running';
  useEffect(() => {
    if (running || latest === undefined) return undefined;
    const id = setTimeout(() => setCheck((c) => c + 1), IDLE_CHECK_MS);
    return () => clearTimeout(id);
  }, [running, latest, check]);

  // A finished run is shown as a replay of its stored entries.
  useEffect(() => {
    if (!latest || running) return undefined;
    let active = true;
    api.get(`/analysis/expert-reports/${latest.id}`)
      .then((res) => { if (active) dispatch({ type: 'reset', state: { run: res.data, entries: res.data.entries || [], finished: true } }); })
      .catch(() => { if (active) setError('Impossible de charger ce débat.'); });
    return () => { active = false; };
  }, [latest, running]);

  // A running run is followed live, reconnecting (and replaying) on errors.
  useEffect(() => {
    if (!latest || !running) return undefined;
    const controller = new AbortController();
    let timer;
    let ended = false;
    // Every connection replays the full state and the reducer is idempotent,
    // so the feed is only reset when a new run is followed.
    dispatch({ type: 'reset', state: {} });
    const connect = () => {
      let rollover = false;
      followExpertRun({
        runId: latest.id,
        signal: controller.signal,
        onEvent: (name, payload) => {
          if (name === 'done') ended = true;
          if (name === 'reconnect') rollover = true;
          else dispatch({ name, payload });
          setConnection('live');
        },
      })
        .then(() => {
          if (ended) {
            setConnection('idle');
            setLatest((l) => (l ? { ...l, status: 'finished-live' } : l));
          } else if (rollover) {
            connect(); // planned end of connection (platform request timeout)
          } else throw new Error('interrupted');
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          setConnection('reconnecting');
          timer = setTimeout(connect, RECONNECT_MS);
        });
    };
    setConnection('live');
    connect();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [latest?.id, running]); // eslint-disable-line react-hooks/exhaustive-deps

  const streams = Object.values(state.streams);
  // Moods only depend on who writes and on stored entries, not on each fragment.
  const sig = moodSignature(state.entries, streams);
  const members = useMemo(() => panelMembers(state.entries, streams), [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  const moods = useMemo(() => computeMoods(state.entries, streams), [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  const scrollToLive = () => revealFeedEnd(feedEnd.current);
  // Called as typed text grows, frame by frame: follow only a reader at the bottom.
  const followIfPinned = useCallback(() => { if (stick.current) revealFeedEnd(feedEnd.current); }, []);
  // New text only scrolls the page when the reader is already at the bottom.
  useEffect(() => {
    if (stick.current) scrollToLive();
  }, [state.entries.length, streams.map((s) => s.text?.length).join()]); // eslint-disable-line react-hooks/exhaustive-deps

  // Whatever element scrolls the page (window or an app container), track
  // whether the reader is at the bottom; scrolls inside a bubble are ignored.
  useEffect(() => {
    const onScroll = (e) => {
      const target = e.target === document || e.target === window ? document.scrollingElement || document.documentElement : e.target;
      if (!target?.contains?.(feedEnd.current)) return;
      // "At the bottom" means the end of the feed is in view: the side column
      // may make the page itself taller than the feed.
      const viewBottom = target === (document.scrollingElement || document.documentElement) ? window.innerHeight : target.getBoundingClientRect().bottom;
      stick.current = feedEnd.current.getBoundingClientRect().top - viewBottom <= NEAR_BOTTOM_PX;
      setFollowing(stick.current);
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', onScroll, { capture: true });
  }, []);

  const live = running && !state.finished;
  const run = state.run || latest;
  const dailyReportId = state.run?.daily_report_id;

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Intelligence"
        title={(
          <>
            Débat en direct
            {live && <span aria-hidden="true" className="h-2.5 w-2.5 animate-pulse-soft rounded-full bg-loss-400 shadow-[0_0_0_4px_rgb(var(--c-loss-400)/0.18)]" />}
          </>
        )}
        icon={Radio}
        iconClassName={live ? 'text-loss-400' : undefined}
        description={(
          <>
            <p>Suivez les experts pendant la génération du rapport quotidien, ou d'un débat lancé à la demande sur une valeur : analyses, table ronde et conclusion, au fil de l'écriture.</p>
            <p className="mt-1 text-sm text-fg-subtle">Regarder le débat ne déclenche aucune analyse.</p>
          </>
        )}
      />

      {error && <Banner tone="loss" role="alert">{error}</Banner>}
      {latest === undefined && !error && (
        <Card><Spinner size={16} label="Recherche du débat du jour…" /></Card>
      )}
      {latest === null && (
        <Card className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between" data-testid="live-empty">
          <p className="text-sm leading-relaxed text-fg-muted">Aucun débat d'experts pour l'instant. Il apparaîtra ici dès la prochaine génération du rapport quotidien.</p>
          <Button as={Link} to="/settings?tab=experts" variant="secondary" size="sm" leadingIcon={Settings2}>Configurer les experts</Button>
        </Card>
      )}

      {run && (
        <Card as="section" className="space-y-5" aria-label="État du débat">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm" data-testid="live-status">
            {live ? (
              <span className="flex items-center gap-2.5">
                <Badge tone="loss" dot="pulse">En direct</Badge>
                <span className="text-fg-body">Débat du {reportDate(run.report_date)}</span>
              </span>
            ) : (
              <span className="text-fg-body">Aucun débat en cours. Dernier débat : {reportDate(run.report_date)}{run.status === 'failed' ? ' (échec du panel)' : ''}</span>
            )}
            {connection === 'reconnecting' && <Badge tone="warn" dot="pulse">Connexion perdue, reconnexion…</Badge>}
            <span className="flex flex-wrap items-center gap-x-4 gap-y-1 sm:ml-auto">
              {dailyReportId && <Button as={Link} to={`/reports/${dailyReportId}`} variant="link" trailingIcon={ArrowRight}>Lire le rapport global</Button>}
              {!live && run.id && <Button as={Link} to={`/reports/experts/${run.id}`} variant="link">Voir l'archive de ce débat</Button>}
            </span>
          </div>
          {run.kind === 'topic' && (
            <p className="flex flex-wrap items-center gap-2 text-sm text-fg-body" data-testid="live-topic">
              <Badge tone="gold">Débat à la demande</Badge>
              <span className="font-medium text-fg">{run.topic_symbol}</span>
              {run.topic_question && <span className="text-fg-muted">« {run.topic_question} »</span>}
            </p>
          )}
          {run.kind === 'pilot' && (
            <p className="flex flex-wrap items-center gap-2 text-sm text-fg-body" data-testid="live-pilot">
              <Badge tone="info">Débat du pilote</Badge>
              <span className="text-fg-muted">Le panel revoit le portefeuille de simulation du pilote.</span>
            </p>
          )}
          <Stepper run={live ? state.run : { phase: 'done' }} kind={run.kind} />
        </Card>
      )}

      {run && (
        // Large screens: the seats and, under them, the speaker's mascot stay
        // in view in a sticky side column while the feed scrolls (see DebateTable).
        <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-6 lg:items-start">
          <aside className="lg:order-2 lg:sticky lg:top-[calc(var(--topbar-h)+1rem)] lg:max-h-[calc(100vh-var(--topbar-h)-2rem)] lg:overflow-y-auto" data-testid="debate-side">
            <DebateTable members={members} moods={moods} />
          </aside>
          <section className="min-w-0 space-y-4 lg:order-1" aria-label="Fil du débat" aria-live="polite">
            {state.entries.map((e) => <Bubble key={`e-${e.id}`} item={e} />)}
            {streams.map((s) => <Bubble key={`s-${s.key}`} item={s} live onGrow={followIfPinned} />)}
            {live && streams.length === 0 && (state.run?.phase === 'research' || !state.run
              ? <ResearchProgress detail={state.run?.phase_detail} />
              : <Spinner size={14} label="En attente de la prochaine intervention…" />
            )}
            <div ref={feedEnd} data-testid="feed-end" />
          </section>
        </div>
      )}

      {live && !following && (
        <div className="fixed bottom-6 left-1/2 z-30 -translate-x-1/2 rounded-control shadow-pop">
          <Button leadingIcon={ArrowDown} onClick={() => { stick.current = true; setFollowing(true); scrollToLive(); }}>
            Revenir au direct
          </Button>
        </div>
      )}
    </div>
  );
};

export default ExpertLive;
